import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../auth/useAuth';
import { knowledgeService } from '../../../services/KnowledgeService';
import { invalidateKnowledgeDatabaseSummaries } from '../knowledgeQueryInvalidation';
import { knowledgeQueryKeys } from '../knowledgeQueryKeys';

function isDocumentsQueryForDatabase(
  queryKey: readonly unknown[],
  databaseId: string,
): boolean {
  return (
    queryKey[0] === 'knowledge' &&
    queryKey[1] === 'documents' &&
    queryKey[2] === databaseId
  );
}

export function useKnowledgeDatabaseQuery(databaseId: string | undefined) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isEnabled = Boolean(user) && Boolean(databaseId);

  useEffect(() => {
    if (!databaseId || !isEnabled) {
      return;
    }

    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'success') {
        return;
      }

      // Only document list fetches should refresh KDB summary counts.
      // Crawl stream patches use setQueryData and must not refetch the KDB.
      if (!isDocumentsQueryForDatabase(event.query.queryKey, databaseId)) {
        return;
      }

      invalidateKnowledgeDatabaseSummaries(queryClient, databaseId);
    });
  }, [databaseId, isEnabled, queryClient]);

  return useQuery({
    queryKey: knowledgeQueryKeys.database(databaseId ?? ''),
    enabled: isEnabled,
    queryFn: () => knowledgeService.getDatabase(databaseId!),
  });
}
